/* SPA structure. See also App.vue */
import { useAnalytics } from '@/composables/useAnalytics'
import { getEnv } from '@/composables/useEnv'
import { useUserStore } from '@/store/user'
import { RolesByPrivilege } from '@/utils/user'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import redirects from './redirects'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'user-view',
    components: {
      root: () => import('@/components/userView/WorkshopsPage.vue'),
      privacyModal: () =>
        import('@/components/modals/privacy/UpdatedTermsModal.vue')
    },
    meta: { accessLevel: RolesByPrivilege.user, pageTitle: 'Home' }
  },
  {
    path: '/onboarding',
    components: {
      root: () => import('@/components/maps/root/RootMap.vue')
    },
    children: [
      {
        name: 'site-select',
        path: 'site-select',
        components: {
          banner: () => import('@/components/banners/SketchBanner.vue'),
          onboarding: () =>
            import('@/components/maps/components/MapOnboarding.vue'),
          parcelSelect: () =>
            import('@/components/maps/layers/ParcelSelectLayer.vue')
        },
        meta: {
          pageTitle: 'Onboarding',
          accessLevel: RolesByPrivilege.public
        }
      }
    ],
    meta: {
      pageTitle: 'Onboarding',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/primers/:primerid?',
    name: 'primer-library',
    components: {
      root: () => import('@/components/primer/PrimerLibrary.vue')
    },
    meta: {
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/primers/consider-remedies',
    name: 'consider-remedies',
    redirect: () => {
      window.location.href =
        'https://livinginfrastructure.org/learn/remedy-explorer'
      return '/'
    }
  },
  {
    path: '/primers/identify-stressors',
    name: 'identify-stressors',
    components: {
      root: () =>
        import('@/components/primer/identify-stressors/IdentifyStressors.vue')
    },
    meta: {
      pageTitle: 'Identify Stressors',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/primers/living-infrastructure-invitation',
    name: 'living-infrastructure-invitation',
    redirect: () => {
      window.location.href = 'https://livinginfrastructure.org/learn/invitation'
      return '/'
    }
  },
  {
    path: '/login',
    name: 'login',
    components: {
      root: () => import('@/components/auth/views/LogInView.vue'),
      collaborateBanner: () =>
        import('@/components/banners/CollaborateBanner.vue')
    },
    meta: {
      pageTitle: 'Login',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/signup',
    name: 'signup',
    components: {
      root: () => import('@/components/auth/views/SignUpView.vue'),
      collaborateBanner: () =>
        import('@/components/banners/CollaborateBanner.vue')
    },
    meta: {
      pageTitle: 'Sign Up',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    components: {
      root: () => import('@/components/auth/views/ForgotPasswordView.vue')
    },
    meta: {
      pageTitle: 'Forgot Password',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/users/password/reset/:token',
    name: 'users-password-reset',
    components: {
      root: () => import('@/components/auth/views/PasswordResetView.vue')
    },
    meta: {
      pageTitle: 'Password Reset',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/workshops/create',
    name: 'workshops-create',
    components: {
      root: () => import('@/components/userView/WorkshopsPage.vue'),
      modal: () => import('@/components/modals/sketches/SketchModal.vue'),
      privacyModal: () =>
        import('@/components/modals/privacy/UpdatedTermsModal.vue')
    },
    meta: { accessLevel: RolesByPrivilege.user, pageTitle: 'Create Sketch' }
  },
  {
    path: '/workshops/edit/:uuid',
    name: 'workshops-edit',
    components: {
      root: () => import('@/components/userView/WorkshopsPage.vue'),
      modal: () => import('@/components/modals/sketches/SketchModal.vue'),
      privacyModal: () =>
        import('@/components/modals/privacy/UpdatedTermsModal.vue')
    },
    meta: { accessLevel: RolesByPrivilege.user, pageTitle: 'Edit Sketch' }
  },
  {
    path: '/workshops/duplicate/:uuid',
    name: 'workshops-duplicate',
    components: {
      root: () => import('@/components/userView/WorkshopsPage.vue'),
      modal: () => import('@/components/modals/DuplicateWorkshopModal.vue'),
      privacyModal: () =>
        import('@/components/modals/privacy/UpdatedTermsModal.vue')
    },
    meta: { accessLevel: RolesByPrivilege.user, pageTitle: 'Duplicate Sketch' }
  },
  {
    path: '/workshops/:uuid',
    components: {
      root: () => import('@/components/maps/root/RootMap.vue'),
      privacyModal: () =>
        import('@/components/modals/privacy/UpdatedTermsModal.vue'),
      contributorModal: () =>
        import('@/components/modals/sharing/ContributorModal.vue')
    },
    meta: { accessLevel: RolesByPrivilege.user },
    children: [
      {
        name: 'select-tool',
        path: 'select-tool',
        components: {
          banner: () => import('@/components/banners/SketchBanner.vue'),
          siteLayers: () =>
            import('@/components/maps/layers/sites/AllSitesLayer.vue'),
          parcelSelect: () =>
            import('@/components/maps/layers/ParcelSelectLayer.vue')
        },
        meta: { pageTitle: 'Sketch' }
      },
      {
        path: 'sketches/:sketchid',
        name: 'sketch-id',
        components: {
          siteLayers: () =>
            import('@/components/maps/layers/sites/SelectedSiteLayer.vue')
        },
        meta: { pageTitle: 'Site' }
      },
      {
        path: 'sketches/:sketchid/edit',
        name: 'sketch-edit',
        components: {
          banner: () => import('@/components/banners/SketchBanner.vue'),
          siteLayers: () =>
            import('@/components/maps/layers/sites/AllSitesLayer.vue')
        },
        meta: { pageTitle: 'Edit Site' }
      },
      {
        path: 'sketches/:sketchid/remedy/:remedyid',
        name: 'remedy-edit',
        components: {
          banner: () => import('@/components/banners/FootprintBanner.vue'),
          siteLayers: () =>
            import('@/components/maps/layers/sites/SelectedSiteLayer.vue')
        },
        meta: { pageTitle: 'Edit Remedy' }
      }
    ]
  },
  {
    path: '/participant/welcome', // re-use old path with new component: no redirect necessary
    name: 'participant-link',
    components: {
      root: () => import('@/components/userView/RequestNewLink.vue')
    },
    meta: {
      pageTitle: 'Request New Link'
    }
  },
  {
    path: '/unauthorized',
    name: 'unauthorized',
    components: {
      root: () => import('@/components/userView/UnauthorizedView.vue')
    },
    meta: {
      pageTitle: 'Unauthorized',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/sketch-closed',
    name: 'sketch-closed',
    components: {
      root: () => import('@/components/userView/PublicSketchClosed.vue')
    },
    meta: {
      pageTitle: 'Sketch Closed',
      accessLevel: RolesByPrivilege.public
    }
  },
  {
    path: '/browser-error',
    name: 'browser-error',
    components: {
      root: () => import('@/components/userView/BrowserErrorView.vue')
    },
    meta: {
      pageTitle: 'Browser Error',
      accessLevel: RolesByPrivilege.public
    }
  }
]

if (getEnv('VITE_APP_EASTER_EGGS') === 'true')
  routes.push({
    path: '/pr1000',
    name: 'pr1000-easter-egg',
    components: {
      root: () =>
        import('@/components/easter-egg/PR1000Game.vue').catch((error) => {
          console.error('Failed to load PR1000Game component:', error)
          return import('@/components/userView/UnauthorizedView.vue') // Fallback component
        })
    },
    meta: {
      pageTitle: 'PR 1000'
    }
  })

const router = createRouter({
  history: createWebHistory(), // Specifying a base parameter in creatingWebHistory() is not needed. This parameter would be provided when deploying the app in a subfolder (aka when the app does not run on the root of a domain, which is not recommended).
  routes: routes.concat(redirects),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

router.beforeEach(async (to, from) => {
  const userStore = useUserStore()
  const { trackPage } = useAnalytics()

  // Token initialization happens here rather than at app mount
  // to ensure it's available before any authentication-related redirects
  if (!userStore.token) {
    await userStore.initializeToken()
  }

  if (typeof to.meta.pageTitle === 'string') {
    document.title = `${to.meta.pageTitle} | Living Infrastructure Field Kit`
    trackPage(to.meta.pageTitle)
  }

  // Redirect authenticated users to participant link redirectTo query
  if (to.name === 'participant-link' && userStore.userIsLoggedIn) {
    return `${to.query.redirectTo}?from=participant-link`
  }

  // Redirect logged in users
  if (
    (to.name === 'login' || to.name === 'signup') &&
    userStore.userIsLoggedIn
  ) {
    return { name: 'user-view', query: { ...to.query } }
  }

  // Redirect guests to login if trying to access user-view
  if (to.name === 'user-view' && userStore.userIsGuest) {
    const route = {
      name: 'login'
    }
    if (from.fullPath !== '/') {
      route['query'] = { redirect: from.fullPath }
    }
    return route
  }
  return true
})

export { routes }

export default router
